import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { ENTERPRISE_TOAST_COMPACT } from "./tokens";

const VARIANT_ICONS = {
  success: CheckCircleOutlineIcon,
  error: ErrorOutlineIcon,
  warning: WarningAmberOutlinedIcon,
  info: InfoOutlinedIcon,
  infoToast: CloudDoneOutlinedIcon,
};

function resolveToastTokens(variant) {
  return ENTERPRISE_TOAST_COMPACT[variant] || ENTERPRISE_TOAST_COMPACT.info;
}

/**
 * Centered full-message view when compact toast text is truncated (or user taps message).
 */
export default function EnterpriseMessageDetailDialog({
  open,
  variant = "info",
  title,
  body,
  actionLabel,
  onAction,
  onClose,
}) {
  const tokens = resolveToastTokens(variant);
  const Icon = VARIANT_ICONS[variant] || InfoOutlinedIcon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="enterprise-message-detail-title"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          mx: 2,
          overflow: "hidden",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: 2.5,
          py: 2,
          backgroundColor: tokens.surface,
          color: tokens.onSurface,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: tokens.iconDisc || tokens.icon,
            color: "#FFFFFF",
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          {title ? (
            <Typography
              id="enterprise-message-detail-title"
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1.35,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                color: tokens.onSurface,
              }}
            >
              {title}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: tokens.muted, mt: -0.5, mr: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2, pt: title ? 2 : 2.5 }}>
        <Typography
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "0.9375rem",
            lineHeight: 1.55,
            color: "#1C1B1F",
            whiteSpace: "pre-wrap",
          }}
        >
          {body}
        </Typography>
      </DialogContent>

      {(actionLabel || onClose) && (
        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: "9999px",
              textTransform: "none",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              color: "#494551",
            }}
          >
            Close
          </Button>
          {actionLabel ? (
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                onAction?.();
                onClose?.();
              }}
              sx={{
                borderRadius: "9999px",
                textTransform: "none",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
                backgroundColor: tokens.iconDisc || tokens.icon,
                color: "#FFFFFF",
                "&:hover": { backgroundColor: tokens.iconDisc || tokens.accent },
              }}
            >
              {actionLabel}
            </Button>
          ) : null}
        </DialogActions>
      )}
    </Dialog>
  );
}
